import { useEffect, useMemo, useState } from 'react';
import { useTracking } from 'react-tracking';
import { useFlag } from '@simplywallst/unleash';

import { useUpdatePublicNarrative } from '../../hooks/useUpdatePublicNarrative';
import type { NarrativeForm } from '../../utils/schema';
import type { Narrative } from '../../hooks/useNarrative';
import { htmlToText } from '@/components/RichTextEditor';
import {
  narrativeGetValuatorQueryParamInitialValues,
  transformFormValuesToValuation,
} from '../../utils/transformation';
import { useNarrativeBack } from '../../hooks/useNarrativeBack';
import { ROUTE_PUBLIC_NARRATIVE_EDIT } from '@/constants/routes';
import { getPath } from '@/router/utils';
import { push } from 'redux-first-router';
import useUser from '@/hooks/useUser';
import NarrativeOnboardingModal from '@/components/NarrativeOnboardingModal';
import { merge } from 'lodash';
import { useNarrativeInvalidate } from '../../hooks/useNarrativeInvalidate';
import { usePublishNarrativeUpdateWithSnackbar } from '../../hooks/usePublishNarrative/usePublishNarrative';
import {
  getInitialUpdateId,
  getInitialContent,
  getInitialTitle,
  getNarrativeId,
  getCompany,
  getOwner,
  getUserHoldsPosition,
  getIsPublishedLocked,
  getIsPublishedWithinGracePeriod,
} from './utils';
import { useUpdateNarrativeUpdateWithSnackbar } from '../../hooks/useUpdateNarrativeUpdate/useUpdateNarrativeUpdate';
import { useNarrativeFormManager } from '../../hooks/useNarrativeFormManager';
import { hasErrors } from '../../hooks/useNarrativeForm';
import { useNarrativeValuationManager } from '../../hooks/useNarrativeValuationFormManager';
import { NarrativeFormContent } from '../NarrativeFormContent';
import { useHasSavedContent } from '../../hooks/useHasSavedContent';
import { useNarrativeInputPayload } from '../../hooks/useNarrativeInputPayload';
import type { MyNarrativeCompany } from '../../hooks/useNarrativeCompany';

type Props = {
  narrative?: Narrative;
  myNarrativeCompany?: MyNarrativeCompany['Company'];
};

export const PublicNarrativeEdit = ({
  narrative,
  myNarrativeCompany,
}: Props) => {
  const {
    enabled: updateNarrativeUpdateEnabled,
    ready: updateNarrativeUpdateReady,
  } = useFlag('narrative-content-from-updates');
  const isUpdateNarrativeUpdateEnabled =
    updateNarrativeUpdateEnabled && updateNarrativeUpdateReady;

  const myNarrative = myNarrativeCompany?.myNarrative;

  const { title, contentBlocks } = narrative ?? {};

  const [narrativeId, setNarrativeId] = useState<string>(
    getNarrativeId(myNarrative, narrative)
  );

  const company = getCompany(myNarrativeCompany, narrative);

  if (!company) throw new Error("Narrative's company is missing");

  const userHoldsPosition = getUserHoldsPosition(myNarrative, narrative);

  const owner = getOwner(myNarrative, narrative);

  const isPublishedLocked = getIsPublishedLocked(myNarrative);

  const isPublishedWithinGracePeriod =
    getIsPublishedWithinGracePeriod(myNarrative);

  const submitLabel = isUpdateNarrativeUpdateEnabled
    ? isPublishedWithinGracePeriod
      ? 'Save'
      : 'Publish'
    : 'Update';

  const { data: user } = useUser();

  const [showingOnboarding, setShowingOnboarding] = useState(
    !Boolean(user.displayName)
  );

  const {
    valuation,
    calculateValuationInitialisation,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    calculateBreakDown,
    calculateFairValue,
    pastAnnualRevenueEarnings,
  } = useNarrativeValuationManager({
    narrative,
    isUpdateNarrativeUpdateEnabled,
    draftUpdate: myNarrative?.draftUpdate,
    latestPublishedUpdate: myNarrative?.latestPublishedUpdate,
    company,
  });

  const { hasSavedContent, setHasSavedContent } = useHasSavedContent();

  const { trackEvent, Track } = useTracking({
    type: 'track',
    subject: 'public-narrative',
    companyId: company.id,
  });

  const { returnPath, goBack } = useNarrativeBack();
  const { invalidate } = useNarrativeInvalidate();

  useEffect(() => {
    if (!showingOnboarding && !user?.displayName) {
      goBack();
    }
  }, [showingOnboarding, user?.displayName, goBack]);

  const goBackWithSuccess = () => {
    const path = getPath({
      type: ROUTE_PUBLIC_NARRATIVE_EDIT,
      payload: { id: narrativeId },
      query: returnPath
        ? {
            company: company.id,
            returnPath,
          }
        : {},
    });

    invalidate();

    trackEvent({
      action:
        isUpdateNarrativeUpdateEnabled && isPublishedWithinGracePeriod
          ? 'save'
          : isUpdateNarrativeUpdateEnabled
            ? 'publish'
            : 'update',
    });

    push(path);
    goBack();
  };

  const [successSnackbar, setSuccessSnackbar] = useState(false);

  const { mutate: updateNarrative, status: updateNarrativeStatus } =
    useUpdatePublicNarrative({
      onSuccess: () => {
        setHasSavedContent(true);
        goBackWithSuccess();
      },
      includeSuccessSnackbar: true,
    });

  const {
    mutate: publishNarrativeUpdateAsync,
    status: publishNarrativeUpdateStatus,
  } = usePublishNarrativeUpdateWithSnackbar({
    includeSuccessSnackbar: successSnackbar,
  });

  const initialUpdateId = getInitialUpdateId(
    myNarrative?.latestPublishedUpdate ?? null,
    myNarrative?.draftUpdate ?? null
  );

  const initialContent = isUpdateNarrativeUpdateEnabled
    ? getInitialContent(
        myNarrative?.draftUpdate ?? null,
        myNarrative?.latestPublishedUpdate ?? null
      )
    : contentBlocks?.[0]?.contentHtml ?? '';

  const initialContentText = htmlToText(initialContent);

  const initialTitle = isUpdateNarrativeUpdateEnabled
    ? getInitialTitle(
        myNarrative?.draftUpdate ?? null,
        myNarrative?.latestPublishedUpdate ?? null
      )
    : title;

  const [narrativeUpdateId, setNarrativeUpdateId] = useState<string | null>(
    initialUpdateId
  );

  const initialNarrativeForm: NarrativeForm = useMemo(() => {
    const calculatorOutputs = valuation
      ? calculateValuationInitialisation({
          valuation,
          calculateFairValueFromInitialisation,
          calculateRevenueGrowthRateFromFairValue,
        })
      : calculateFairValueFromInitialisation();

    return merge(
      {
        ...calculatorOutputs,
        userHoldsPosition: userHoldsPosition ?? false,
        title: initialTitle ?? '',
        content: {
          text: initialContentText ?? '',
          html: initialContent ?? '',
        },
      },
      narrativeGetValuatorQueryParamInitialValues()
    );
  }, [
    calculateValuationInitialisation,
    valuation,
    calculateFairValueFromInitialisation,
    calculateRevenueGrowthRateFromFairValue,
    userHoldsPosition,
    initialTitle,
    initialContentText,
    initialContent,
  ]);

  const {
    values,
    errors,
    setSubmitCount,
    validateForm,
    resetForm,
    handleContentChange,
    handleHeaderChange,
    handleSetFairValue,
    handleSetPartialValues,
  } = useNarrativeFormManager({
    initialNarrativeForm,
    calculateFairValue,
    calculateRevenueGrowthRateFromFairValue,
    trackEvent,
    isUpdateNarrativeUpdateEnabled,
    setHasSavedContent,
    narrativeUpdateId,
    narrativeId,
    companyId: company.id,
    successSnackbar,
    setNarrativeUpdateId,
    disableAutoSave: isPublishedWithinGracePeriod,
    setNarrativeId,
  });

  const {
    mutate: updateNarrativeUpdateAsync,
    status: updateNarrativeUpdateStatus,
  } = useUpdateNarrativeUpdateWithSnackbar({
    includeSuccessSnackbar: successSnackbar,
  });

  const narrativeInputPayload = useNarrativeInputPayload({
    values,
    narrativeId,
    companyId: company.id,
  });

  const handleSubmit = async () => {
    setSubmitCount((c) => c + 1);

    if (hasErrors(validateForm())) return;

    if (isUpdateNarrativeUpdateEnabled && narrativeUpdateId) {
      await updateNarrativeUpdateAsync({
        id: narrativeUpdateId,
        input: {
          contentHtml: values.content.html,
          title: values.title,
          valuation: {
            ...transformFormValuesToValuation({ values }),
          },
        },
      });

      if (
        !isPublishedLocked &&
        !isPublishedWithinGracePeriod &&
        myNarrative?.draftUpdate !== null
      ) {
        await publishNarrativeUpdateAsync({
          id: narrativeUpdateId,
          input: { userHoldsPosition: values.userHoldsPosition ?? false },
        });
      }

      goBackWithSuccess();
    } else if (isUpdateNarrativeUpdateEnabled) {
      // Shouldn't get to this, if this runs means backend has not migrated to use update model
      // and there is no narrativeUpdateId
      throw new Error(
        `Backend has not migrated to use update model, narrativeId: ${narrativeId}`
      );
    } else {
      updateNarrative({
        input: { ...narrativeInputPayload, id: narrativeId ?? '' },
      });
    }

    setSubmitCount(0);
  };

  // If there's new financials or fiscal data, we need to update the valuation
  // and re-calculate the valuation
  if (showingOnboarding)
    return (
      <Track>
        <NarrativeOnboardingModal
          companyId={company.id}
          isOpen={showingOnboarding}
          onClose={() => setShowingOnboarding(false)}
        />
      </Track>
    );

  const isLoading = isUpdateNarrativeUpdateEnabled
    ? updateNarrativeUpdateStatus === 'pending' ||
      publishNarrativeUpdateStatus === 'pending'
    : updateNarrativeStatus === 'pending';

  const cancelLabel = isUpdateNarrativeUpdateEnabled
    ? myNarrative?.draftUpdate !== undefined
      ? 'Close draft'
      : 'Close' // Can be within grace period
    : 'Cancel';

  return (
    <Track>
      <NarrativeFormContent
        initialFairValue={initialNarrativeForm.fairValue}
        errors={errors}
        values={values}
        onChange={handleSetPartialValues}
        onSetFairValue={handleSetFairValue}
        onContentChange={handleContentChange}
        onHeaderChange={handleHeaderChange}
        isLoading={isLoading}
        onSubmit={handleSubmit}
        submitLabel={submitLabel}
        company={company as any}
        initialContentHtml={initialContent}
        owner={owner}
        onReset={resetForm}
        pastAnnualRevenueEarnings={pastAnnualRevenueEarnings}
        calculationBreakDownOutput={calculateBreakDown({
          discountRatePercentage: values.discountRatePercentage,
          futurePE: values.priceEarningsMultiple,
          profitMarginPercentage: values.profitMarginPercentage,
          revenueGrowthRatePercentage: values.revenuePercentage,
          timeFrame: values.timePeriod,
        })}
        hasSavedContent={hasSavedContent}
        setSuccessSnackbar={setSuccessSnackbar}
        isPublishedLocked={
          isPublishedLocked && myNarrative?.draftUpdate !== undefined
        }
        cancelLabel={cancelLabel}
      />
    </Track>
  );
};
